<template>
  <div v-if="displayMode.selected == 'tiles'">
    <b-row>
      <b-col cols="4">
        <search-input v-model="searchText"
                      :placeholder="$t('Webportal.SearchTextFilter.Placeholder')"
                      @change="loadData" />
      </b-col>
      <b-col>
        <div v-show="retailerValues" class="input-group d-flex justify-content-end" >
          <b-pagination v-model="selectedPage"
                        :total-rows="RowCount"
                        :per-page="pageSize"
                        :limit="5"
                        :disabled="false"
                        class="mb-0" />
          <span>
            <b-button variant="primary" :disabled="false">{{
              $n(RowCount)
            }}</b-button>
          </span>
        </div>
      </b-col>
    </b-row>

    <b-row class="mx-2" align-v="start">
      <div v-if="dynamicCardData.length == 0">
        {{ $t("Webportal.NoDataFound") }}
      </div>
      <b-col  v-for="(retailer, index) in dynamicCardData"
              :key="index"
              cols="2"
              style="width: 300px; max-width: 300px">
        <Card :cardData="retailer"
              :colorRange="colorRange"
              @DrilldownAction="loadDashboard"
              :defaultLogo="defaultLogo" />
      </b-col>
    </b-row>
  </div>

  <div v-else>
    <CxDashboardHeader  v-if="retailerValues"
                        :marginOverview="marginOverview"
                        :focusKpis="retailerValues.FocusKpi"
                        :scoreInfo="getOverallScore"
                        :showRetailers="true"
                        :theme="theme"
                        :threshold="colorRange"
                        />

    <div style="width: 100%;">
      <CustomDataGrid ref="grid"
                      :rowData="rowData"
                      :columnDefs="cxColumns"
                      :customColumns="customColumns"
                      :defaultColDef="defaultColDef"
                      :pinnedTopRowData="pinnedTopRowData"
                      :theme="theme"
                      :gridOptions="gridOptions" />
    </div>
  </div>
</template>

<script>
  import CancelToken from "@/mixins/CancelToken";
  import DashboardService from "@/services/DashboardService";
  import EventBus from "@/services/EventBus.js";
  
  import Card from "./Card";
  import SearchInput from "@/components/SearchInput";
  import SearchSelect from "@/components/SearchSelect";

  import CheckBoxGrid from '@/components/CX/Grid/CheckBoxGrid';
  import CustomDataGrid from '@/components/CX/Grid/CustomDataGrid';
  import RetailerTableColumns from '@/mixins/jlr/RetailerTableColumns';
  import CxDashboardHeader from './Dashboard/CxDashboardHeader.vue';

  const PAGE_SIZE_DEFAULT = 12;
  const PAGE_SIZE_MAXIMUM = 999999;

export default {
  mixins: [ CancelToken, RetailerTableColumns ],

  components: {
    Card, CheckBoxGrid, CustomDataGrid, CxDashboardHeader,
    SearchInput, SearchSelect
  },

  props: {
    clientId:       { type: Number, default: () => NaN },
    colorRange:     { type: Array,  default: () => [] },
    countryId:      { type: String, default: () => '' },
    defaultLogo:    { type: String, default: () => undefined },
    displayMode:    { type: Object, default: () => undefined },
    overall:        { type: Array,  default: () => [] },
    retailerValues: { type: Object, default: () => {} },
    scoreId:        { type: Number, default: () => -1 },
    theme:          { type: String, default: () => 'light' }
  },

  data() {
    return {
      isMounted: false,
      cxColumns: [],

      searchText: null, // Full text search text

      // Paging properties
      pageSize: PAGE_SIZE_DEFAULT, // Number of items on one page of the table
      selectedPage: 1, // Number of the page, that is currently visible
      RowCount: 0,
      
      marginOverview: null,
      marketCxScores: [],
      marketExplorationScores: [],
      marketNonExplorationScores: [],
      
      // // Inject rows into CustomDataGrid
      // rowData: []... from Mixin:

      // CustomDataGrid ... from Mixin: ...TableColumns.Data
      // gridOptions: ... from Mixin:  ...TableColumns.Data { suppressAggFuncInHeader: true },
      // columnDefs ... fixed Columns
      // customColumns ... custom Columns
      // defaultColDef ... default properties for Columns
      // dynamicColumnDefs ... from Mixin: ...TableColumns.computed 
    };
  },

  computed: {
    // Prepare aggregated data
    dynamicCardData: function () {
      if (!this.retailerValues?.AggregatedData) return [];

      return this.retailerValues.AggregatedData.map((data) =>
        ({
          Title: data.Name,
          Subtitle: data.CustomLocationId,
          Image: data.HasLocationImage
            ? `${process.env.BASE_URL}api/dashboard/location-image/${data.ClientLocationId}`
            : null,
          AdditionalInfo: `${data.Address}<br>${data.PostalCode} ${data.City}`,
          Value: data.CXScore,
          ActionsToTake:
            (data?.ExplorationActionsToTake || 0) +
            (data?.KpiActionsToTake || 0) +
            (data?.AFSActionsToTake || 0),
          Drilldown: data.ClientLocationId
        })
      );
    },

    dynamicColumnDefs() {
      let items = [...this.columnDefs].map(this.setupLocalColumn);
      if (this.isMounted && this.customColumns && this.customColumns.active && this.customColumns.active.length > 0) {
        items.push(...this.customColumns.active.map(x => { return this.mixinGetColumn(x) } ).filter(x => x !== undefined));
        this.$store.commit('retailers', this.customColumns.active);
      }
      return items;
    },

    getOverallScore() {
      return {
        showTreeSelect: false,
        showSelector: false,
        scoreData: this.overall[0].results.find((o) => o.name === "CX Score"),
        threshold: this.colorRange
      };
    },
  },

  watch: {
    selectedPage: {
      handler(newVal, oldVal) {
        this.loadData();
      },
    },
    retailerValues: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.tableCancelToken = this.initCancelToken(this.tableCancelToken);
          this.getCxTableStructure(
            this.tableCancelToken, 
            { countryId: this.countryId, clientId: this.clientId, tableId: 'retailer' }
          );

          this.RowCount = newVal.TotalRowCount;
          this.rowData = newVal?.AggregatedData;
          this.marginOverview = _.isEmpty(newVal?.MarginOverview) ? null : newVal.MarginOverview;
          this.pinnedTopRowData = this.applyTopLineFix(_.last(this.overall), true); 
        }
      },
    },
  },

  mounted() {
    EventBus.$on("ClientChanged", this.resetSearchText);
    EventBus.$on("show-retailer-details-clicked", this.loadDashboard);
    EventBus.$on("grid-selected-custom-columns-changed", this.selectedCustomColumns);
    this.isMounted = true;
    this.canHaveAccomplished = true;
  },

  updated() {
    if (this.clientChange) {
      this.searchText = null;
    }
  },

  beforeDestroy() {
    this.$store.commit('retailers', this.customColumns.active);
    EventBus.$off("ClientChanged", this.resetSearchText);
    EventBus.$off("show-retailer-details-clicked", this.loadDashboard);
    EventBus.$off("grid-selected-custom-columns-changed", this.selectedCustomColumns);
  },

  methods: {
    selectedCustomColumns(params) {
      if (this.customColumns && this.customColumns.active)
        this.customColumns.active = params;
    },


    resetSearchText(e) {
      this.searchText = null;
    },

    loadDashboard(drilldownArg) {
      this.$emit("drilldownToDashboard", drilldownArg);
    },

    loadData() {
      this.$emit("loadData", this.searchText, this.selectedPage, this.pageSize);
    },

    determineValueColor(value) {
      if (!this.colorRange) return null;

      return this.colorRange.find(
        (thres) => thres.Minimum <= value && thres.Maximum >= value
      )?.Color;
    },

    onSearchTextChanged(value) {
      this.$emit("update:searchText", value);
    }
  }
}

</script>
